<script setup>
  import { computed, ref } from 'vue'
  import { useOrderFormStore } from '../stores/order-form'
  import { errorMessages, formValidator } from '../utils/form-validator'
  import { registerNewUser, createPayment } from '../services/api'
  import { getPlanPayload } from '../utils/plans'
  const store = useOrderFormStore()

  const errorClass = 'error'
  const password = ref(null)
  const password2 = ref(null)
  const isPassValid = ref(true)
  const formError = ref('')
  const isConsentsAccepted = ref(false)
  const isSubmitDisabled = computed(() => Object.values(store.getClientDetails).some(val => !val))

  const handlePayment = async () => {
    const { translatedName, ...paymentPayload } = getPlanPayload()
    const body = {
      ...paymentPayload,
      email: store.getClientEmail
    }
    try {
      const { success, redirect } = await createPayment(body)

      if (!success) {
        throw new Error(errorMessages.GENERAL)
      }

      window.location.href = redirect

    } catch (error) {
      formError = error
      console.error(error)
    }
  }

  const onSubmit = async () => {
    if (isSubmitDisabled.value) {
      return
    }

    if (!isConsentsAccepted.value) {
      formError.value = errorMessages.MISSING_CONSENTS
      return
    }

    formError.value = ''

    try {
      const { password2, ...newUserDetails } = store.getClientDetails

      const {
        success: userRegisteredSuccessfuly,
        error
      } = await registerNewUser(newUserDetails)

      if (userRegisteredSuccessfuly) {
        await handlePayment()
      } else {
        formError.value = error
        throw new Error(error)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const onChange = ({target}) => {
    switch (target.name) {
      case 'username':
        isEmailValid(target);
        break;
      case 'password':
      case 'password2':
        isPasswordValid();
        break;
      default:
        store.updateClientDetails({[target.name]: target.value})
        break;
    }
  }

  const isEmailValid = (el) => {
    const errorNotice = formValidator.validateEmail(el.value, true)
    if (errorNotice) {
      el.classList.add(errorClass);
      store.updateClientDetails({ username: '' })
      formError.value = errorNotice
    } else {
      el.classList.remove(errorClass);
      store.updateClientDetails({ username: el.value })
      formError.value = ''
    }
  }

  const isPasswordValid = () => {
    const errorNotice = formValidator.validatePassword(password.value, password2.value, true)
    if (errorNotice) {
      store.updateClientDetails({
        password: '',
        password2: ''
      })
      isPassValid.value = false
      formError.value = errorNotice
    } else {
      store.updateClientDetails({
        password: password.value,
        password2: password2.value
      })
      isPassValid.value = true
      formError.value = ''
    }
  }
</script>

<template>
  <div>
    <div class="offer-name">
      Wybrana oferta: <strong>{{ getPlanPayload().translatedName  }}</strong>
    </div>
    <form
      ref="formRef"
      @submit.prevent="onSubmit"
      @change="onChange"
      id="form"
      name="form"
    >
      <div class="form-input-wrapper">
        <div class="form-input">
          <input
            type="email"
            @change="validateEmail"
            placeholder="e-mail (login) *"
            name="username"
            required
          />
        </div>

        <div class="form-input">
          <input
            type="text"
            placeholder="imię *"
            name="firstName"
            required
          />
        </div>

        <div class="form-input">
          <input
            type="text"
            placeholder="nazwisko *"
            name="lastName"
            required
          />
        </div>

        <div class="form-input">
          <input
            type="password"
            @change="validatePassword"
            placeholder="hasło *"
            name="password"
            v-model="password"
            v-bind:class="{error: !isPassValid}"
            required
          />
        </div>

        <div class="form-input">
          <input
            type="password"
            @change="validatePassword"
            placeholder="powtórz hasło *"
            name="password2"
            class="rounded-full"
            v-model="password2"
            v-bind:class="{error: !isPassValid}"
            required
          />
        </div>
      </div>

      <div class="form-notice">
        <label>
          <input type="checkbox" v-model="isConsentsAccepted" required name="consents"/> Przechodząc do następnego kroku oświadczasz, że znasz i akceptujesz <a href="https://zacznijzsiwa.pl/regulamin/" target="_blank">Regulamin</a> oraz <a href="https://zacznijzsiwa.pl/polityka-prywatnosci/" target="_blank">Politykę prywatności</a></label>
        <div class="form-error" v-if="formError">
          {{ formError }}
        </div>
      </div>


      <div class="wm-flex wm-center">
        <button type="submit" :disabled="isSubmitDisabled">Następny krok</button>
      </div>

      <div class="wm-flex wm-center form-notice form-notice-login">
        <a href="https://zacznijzsiwa.pl/login" target="_blank">Masz już konto? Zaloguj się</a>
      </div>
    </form>
  </div>
</template>

<style scoped>
.offer-name {
  padding-bottom: 1rem;
  font-size: 1.125rem;
}

input {
  border-radius: 16px !important;
  border: 1px solid rgb(220, 237, 164);
  color: rgb(82, 125, 31);
  background-color: rgb(248, 251, 237);
  padding: 1rem 1.25rem;
  font-weight: 500;
}

input:focus, input:active, input:focus:active, input:focus-visible {
  border-color: rgb(82, 125, 31);
  outline: none;
}

::placeholder {
  color: rgb(220 237 164);
  opacity: 1;
}

.form-input-wrapper {
  display: flex;
  flex-direction: column;
  row-gap: 1.2rem;
  padding-bottom: 2rem;
}

.form-input {
  display: flex;
}

button {
  background:rgb(82, 125, 31);
  border: 1px solid rgb(82, 125, 31);
  color: #fff;
  font-weight: 600;
  border-radius: 16px;
  width: 100%;
  max-width: 420px;
  padding: 1rem 1.5rem;
}

button:hover, button:focus {
  background: rgb(54 82 20);
}

.form-notice {
  padding-bottom: 2rem;
  font-size: .9rem;
}

.form-notice-login {
  font-weight: 500;
  padding-top: 2rem;
}

.error {
  border-color: rgb(255, 23, 23);
}

.form-error {
  padding-top: 1rem;
  color: rgb(255, 23, 23);
}
</style>